// crewbit-admin/src/pages/SetupGuide/items/InvoiceBrandingItem.tsx
//
// SetupGuide → Customise your invoice. Thin shell over the shared
// <InvoiceBrandingForm /> per §SESSION_INVOICE_SETTINGS_LEGACY_RETIREMENT
// D8. The form owns the field set + per-field blur-save persistence; this
// shell preserves the SetupGuide chrome (breadcrumb + title + lead +
// Save/Cancel buttons) and the resolveInvoiceBrandingItem completion
// criterion (logo_url IS NOT NULL).

import { ChevronLeft } from 'lucide-react';
import { admin, palette, radii, typography } from '../../../styles/tokens';
import { InvoiceBrandingForm } from '../../../components/money/branding/InvoiceBrandingForm';

interface InvoiceBrandingItemProps {
  businessId: string;
  onBack: () => void;
  onSaved: () => void;
}

export default function InvoiceBrandingItem({
  businessId,
  onBack,
  onSaved,
}: InvoiceBrandingItemProps) {
  const handleSaveAndContinue = () => {
    // Flush any pending edit in the focused field. The form's per-field
    // blur-save runs on the resulting blur, so subsequent click work
    // happens after the persist resolves on the next event-loop tick.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // setTimeout 0 lets the blur-fired persist queue before we mark the
    // setup-guide item complete. The completion resolver re-reads the row
    // (logo_url IS NOT NULL); the operator-visible edits land regardless
    // because blur-save runs before the resolver requery.
    setTimeout(onSaved, 0);
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '6px 8px 6px 4px',
          marginBottom: 16,
          background: 'transparent',
          border: 'none',
          color: admin.caption,
          fontSize: typography.size.caption,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <ChevronLeft size={16} />
        <span>Setup guide</span>
        <span style={{ color: palette.g400, margin: '0 4px' }}>›</span>
        <span style={{ color: admin.body }}>Customise your invoice</span>
      </button>

      <h1
        style={{
          margin: 0,
          fontSize: typography.size.title,
          fontWeight: typography.weight.bold,
          color: admin.header,
          letterSpacing: '-0.01em',
        }}
      >
        Customize your invoice
      </h1>
      <p
        style={{
          margin: '8px 0 24px',
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
          maxWidth: 560,
        }}
      >
        This is what clients see when you send them an invoice. Make it look like your business.
      </p>

      <InvoiceBrandingForm businessId={businessId} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
        <button
          type="button"
          onClick={handleSaveAndContinue}
          style={{
            padding: '10px 20px',
            borderRadius: radii.md,
            border: 'none',
            background: palette.accent,
            color: palette.white,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Save &amp; continue
        </button>
        <button
          type="button"
          onClick={onBack}
          style={{
            padding: '10px 16px',
            borderRadius: radii.md,
            border: 'none',
            background: 'transparent',
            color: admin.caption,
            fontSize: typography.size.body,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
